/**
 * The flat-fee value story, in one place so every surface tells the same,
 * honest story. A GoReal buyer pays a flat fee instead of a percentage
 * commission; the money kept is the commission that would have applied minus
 * that flat fee (never negative).
 *
 * Commission is a RANGE in the Bay Area, not a single number. We headline the
 * CONSERVATIVE low end (2.5%) so a shown figure is a floor, not a ceiling — at
 * 3% a buyer keeps even more. Worked example (the canonical one): a $1.2M home
 * at 2.5% is a $30,000 commission, so ~$25,000 stays with the buyer.
 */
export const FLAT_FEE = 5_000;

/** Typical Bay Area buyer's-agent commission runs 2.5%–3%. */
export const COMMISSION_LOW = 0.025;
export const COMMISSION_HIGH = 0.03;

/**
 * Money a buyer keeps vs. a percentage commission, floored at 0.
 * Defaults to the conservative 2.5% end so the figure never over-promises.
 */
export function savingsVsCommission(price: number, rate: number = COMMISSION_LOW): number {
  if (!(price > 0)) return 0;
  return Math.max(price * rate - FLAT_FEE, 0);
}

/** The commission itself (no flat fee subtracted) at `rate`. */
export function commissionAt(price: number, rate: number = COMMISSION_LOW): number {
  if (!(price > 0)) return 0;
  return price * rate;
}

/** Low/high savings across the 2.5%–3% commission range, floored at 0. */
export function savingsRange(price: number): { low: number; high: number } {
  return {
    low: savingsVsCommission(price, COMMISSION_LOW),
    high: savingsVsCommission(price, COMMISSION_HIGH),
  };
}
